import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { getApiUrlString } from '@shared/utility/webFactory';
import { ApiCallService } from '@services/web-serivces/api-call.service';
import {
  ConfigResponse,
  EmailURLConfigurationResponse,
  MenuConfiguration,
  // MenuItem lint issue,
  UpdateEmailURLConfigurationPayload,
} from '../interfaces/admin-url.interface';
import {
  NationalThresholdFeature,
  NationalThresholdParameter,
  NationalThresholdRawThreshold,
} from '../interfaces/admin-national-threshold.interface';
import { MenuStructure } from '@shared/interfaces/interface';
import { MenuConfigurationService } from 'src/app/services/shared/menu-configuration/menu-configuration.service';

/**
 * Service for handling administrative API requests.
 */
@Injectable({
  providedIn: 'root',
})
export class AdminService {
  api = inject(ApiCallService);

  private readonly adminEmailsandUrls = new BehaviorSubject<EmailURLConfigurationResponse>({
    urls: [],
    emails: [],
  });
  adminEmailsandUrls$ = this.adminEmailsandUrls.asObservable();

  private readonly menuItems = new BehaviorSubject<MenuConfiguration>({
    menuItems: [],
  });
  menuItems$ = this.menuItems.asObservable();
  private readonly isAdminFormChanged = new BehaviorSubject<boolean>(false);
  isAdminFormChanged$ = this.isAdminFormChanged.asObservable();
  menuService = inject(MenuConfigurationService);

  private menuConfigUpdatedSource = new Subject<void>;
  menuConfigUpdated$ = this.menuConfigUpdatedSource.asObservable();

  /**
   * Constructor to inject HttpClient.
   * @param http HttpClient instance for making HTTP requests.
   */
  constructor(private readonly http: HttpClient) {}
  /**
   * Retrieves URL email configuration.
   * @returns Observable containing URL email data.
   */
  getURLEmail() {
    return this.http.get('admin/configuration/geturlemail');
  }

  /**
   * Updates the menu configuration.
   * @param payload The data to update the menu configuration.
   * @returns Observable containing the response from the API.
   */
  updateMenuConfiguration(payload: MenuConfiguration): Observable<ConfigResponse> {
    return this.api.singleApiCall(
      `${getApiUrlString('updateMenuConfiguration')}`,
      'PUT',
      payload,
      null,
      null,
      true,
    );
  }

  /**
   * Retrieves the menu configuration from the backend.
   * @returns Observable containing menu configuration data.
   */
  getMenuConfiguration(): Observable<MenuConfiguration> {
    return this.api.singleApiCall(
      `${getApiUrlString('getMenuConfiguration')}`,
      'GET',
      null,
      null,
      null,
      true,
    );
  }

  /**
   * Retrieves the National Threshold configuration from the backend.
   * @returns Observable containing National Threshold configuration data.
   */
  getListFeatures(): Observable<{ features: NationalThresholdFeature[] }> {
    return this.api.singleApiCall(
      `${getApiUrlString('getListFeatures')}`,
      'GET',
      null,
      null,
      null,
      true,
    );
  }

  getListParameters(featureID: number): Observable<{ parameters: NationalThresholdParameter[] }> {
    return this.api.singleApiCall(
      `${getApiUrlString('getListParameters')}${featureID}/parameters`,
      'GET',
      null,
      null,
      null,
      true,
    );
  }

  getThresholdData(
    featureID: number,
    parameterID: number,
  ): Observable<{ thresholds: NationalThresholdRawThreshold[] }> {
    return this.api.singleApiCall(
      `${getApiUrlString('getThresholdData')}${featureID}/parameters/${parameterID}`,
      'GET',
      null,
      null,
      null,
      true,
    );
  }

  getEmailUrlConfiguration(): Observable<EmailURLConfigurationResponse> {
    return this.api.singleApiCall(
      `${getApiUrlString('getAppConfiguration')}`,
      'GET',
      null,
      null,
      null,
      true,
    );
  }

  updateEmailUrlConfiguration(
    payload: UpdateEmailURLConfigurationPayload,
  ): Observable<ConfigResponse> {
    return this.api.singleApiCall(
      `${getApiUrlString('updateEmailUrlConfiguration')}`,
      'PUT',
      payload,
      null,
      null,
      true,
    );
  }

  setAdminEmailsandUrls(config: EmailURLConfigurationResponse): void {
    this.adminEmailsandUrls.next(config);
  }

  setMenuItems(menuItemsList: MenuConfiguration): void {
    this.menuItems.next(menuItemsList);
  }


  setAdminFormStatus(status: boolean): void {
    this.isAdminFormChanged.next(status);
  }

  getAdminFormStatus(): boolean {
    return this.isAdminFormChanged.getValue();
  }

  getSeverity(parameterId: number,) {
    return this.api.singleApiCall(
      `${getApiUrlString('getSeverity')}/${parameterId}/severity`,
      'GET',
      null,
      null,
      null,
      true,
    );
  }

  
  fetchAdminEmailsAndUrls(): void {
    this.menuService.getAppConfiguration().subscribe((data: MenuStructure) => {
      const emailUrlConfig: EmailURLConfigurationResponse = {
        urls: data.urls,
        emails: data.emails,
      };
      this.setAdminEmailsandUrls(emailUrlConfig);
    });
  }

  notifyMenuConfigUpdated() {
    this.menuConfigUpdatedSource.next();
  }

}
